// client/src/components/donations/DonationsPage.jsx
//
// /:tenant/donations (Roadmap Phase 5.1):
//   - Pages: donation pages / campaigns, goal progress, public link + embed
//   - Gifts: every gift, refund, resend receipt
//   - Monthly donors: active monthly gifts, stop one
//   - Receipts: thank-you text and the tax statement on every receipt
//   - Year-end: per-donor giving summaries for a year, preview and email
//
// API: /api/donations/:tenant (server/routes/donationRoutes.js).

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaPlus, FaEdit, FaTrash, FaCog } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import DesktopActionBar from '../DesktopActionBar';
import LoadingState from '../common/LoadingState';
import GoalProgressBar from './GoalProgressBar';
import { getJson } from '../../services/http';
import {
  getDonationSettings, saveDonationSettings, listDonationPages, createDonationPage, updateDonationPage, deleteDonationPage,
  listDonations, refundDonation, resendReceipt, listMonthlyDonors, cancelMonthlyGift,
  listYearSummaries, previewYearSummary, sendYearSummaries, formatMoney, errorText,
} from '../../services/donationsApi';
import '../publishing/Publishing.css';
import './Donations.css';

function when(value) {
  if (!value) return '';
  const s = String(value);
  const d = new Date(s.includes('T') ? s : `${s.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString();
}
const dollars = (cents) => (cents === null || cents === undefined || cents === '' ? '' : String(Number(cents) / 100));
const toCents = (v) => (v === '' || v === null || v === undefined ? null : Math.round(Number(v) * 100));

const EMPTY_PAGE = {
  title: '', description: '', goal: '', amounts: '25, 50, 100', allowCustom: true, min: '5', allowMonthly: true,
  offerCoverFees: true, allowDedication: true, eventId: '', showOnHub: true, thankYouMessage: '', active: true,
};

function PageForm({ initial, events, onSave, onCancel, t }) {
  const [f, setF] = useState(() => (initial ? {
    title: initial.title, description: initial.description || '', goal: dollars(initial.goalCents),
    amounts: initial.suggestedAmounts.map((c) => dollars(c)).join(', '), allowCustom: initial.allowCustom, min: dollars(initial.minCents),
    allowMonthly: initial.allowMonthly, offerCoverFees: initial.offerCoverFees, allowDedication: initial.allowDedication,
    eventId: initial.eventId || '', showOnHub: initial.showOnHub, thankYouMessage: initial.thankYouMessage || '', active: initial.active,
  } : EMPTY_PAGE));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await onSave({
        title: f.title, description: f.description, goalCents: toCents(f.goal),
        suggestedAmounts: f.amounts.split(',').map((a) => a.trim()).filter(Boolean).map((a) => toCents(a.replace(/[^0-9.]/g, ''))),
        allowCustom: !!f.allowCustom, minCents: toCents(f.min) || 100, allowMonthly: !!f.allowMonthly,
        offerCoverFees: !!f.offerCoverFees, allowDedication: !!f.allowDedication,
        eventId: f.eventId ? Number(f.eventId) : null, showOnHub: !!f.showOnHub, thankYouMessage: f.thankYouMessage, active: !!f.active,
      });
    } catch (err) {
      setError(errorText(err, t('donations.page.saveError', 'The donation page could not be saved.')));
      setBusy(false);
    }
  };

  return (
    <form className="pb-form" onSubmit={submit} data-testid="page-form">
      <label className="pb-field pb-wide" htmlFor="dp-title">
        {t('donations.page.title', 'Title')}
        <input id="dp-title" value={f.title} onChange={(e) => set('title', e.target.value)} required maxLength={160} />
      </label>
      <label className="pb-field pb-wide" htmlFor="dp-desc">
        {t('donations.page.description', 'What the money is for')}
        <textarea id="dp-desc" rows={3} value={f.description} onChange={(e) => set('description', e.target.value)} />
      </label>
      <label className="pb-field" htmlFor="dp-goal">
        {t('donations.page.goal', 'Goal in dollars (blank = no goal bar)')}
        <input id="dp-goal" type="number" min="1" step="1" value={f.goal} onChange={(e) => set('goal', e.target.value)} />
      </label>
      <label className="pb-field" htmlFor="dp-amounts">
        {t('donations.page.amounts', 'Suggested amounts in dollars (comma-separated)')}
        <input id="dp-amounts" value={f.amounts} onChange={(e) => set('amounts', e.target.value)} />
      </label>
      <label className="pb-field" htmlFor="dp-min">
        {t('donations.page.min', 'Smallest gift in dollars')}
        <input id="dp-min" type="number" min="1" step="1" value={f.min} onChange={(e) => set('min', e.target.value)} />
      </label>
      <label className="pb-field" htmlFor="dp-event">
        {t('donations.page.event', 'Show the goal bar on this event’s page')}
        <select id="dp-event" value={f.eventId} onChange={(e) => set('eventId', e.target.value)}>
          <option value="">{t('donations.page.noEvent', 'No event')}</option>
          {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
        </select>
      </label>
      <div className="pb-checks pb-wide">
        <label><input type="checkbox" checked={f.allowCustom} onChange={(e) => set('allowCustom', e.target.checked)} /> {t('donations.page.allowCustom', 'Donors can enter any amount')}</label>
        <label><input type="checkbox" checked={f.allowMonthly} onChange={(e) => set('allowMonthly', e.target.checked)} /> {t('donations.page.allowMonthly', 'Offer monthly giving')}</label>
        <label><input type="checkbox" checked={f.offerCoverFees} onChange={(e) => set('offerCoverFees', e.target.checked)} /> {t('donations.page.coverFees', 'Offer “cover the fees”')}</label>
        <label><input type="checkbox" checked={f.allowDedication} onChange={(e) => set('allowDedication', e.target.checked)} /> {t('donations.page.dedication', 'Allow “in honor of / in memory of”')}</label>
        <label><input type="checkbox" checked={f.showOnHub} onChange={(e) => set('showOnHub', e.target.checked)} /> {t('donations.page.showOnHub', 'Show on your hub page')}</label>
        <label><input type="checkbox" checked={f.active} onChange={(e) => set('active', e.target.checked)} /> {t('donations.page.active', 'Page is live')}</label>
      </div>
      <label className="pb-field pb-wide" htmlFor="dp-thanks">
        {t('donations.page.thanks', 'Thank-you message (shown after giving and in the receipt)')}
        <textarea id="dp-thanks" rows={2} value={f.thankYouMessage} onChange={(e) => set('thankYouMessage', e.target.value)} maxLength={1000} />
      </label>
      {error && <div className="note error pb-wide" role="alert">{error}</div>}
      <div className="pb-actions pb-wide">
        <button type="submit" className="pb-btn primary" disabled={busy}>{t('donations.page.save', 'Save page')}</button>
        <button type="button" className="pb-btn" onClick={onCancel}>{t('common.cancel', 'Cancel')}</button>
      </div>
    </form>
  );
}

function CopyField({ label, value, t }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* no clipboard */ }
  };
  return (
    <label className="dn-copy">
      <span className="visually-hidden">{label}</span>
      <input readOnly value={value} aria-label={label} onFocus={(e) => e.target.select()} />
      <button type="button" className="pb-btn" onClick={copy}>{copied ? t('donations.copied', 'Copied') : t('donations.copy', 'Copy')}</button>
    </label>
  );
}

const embedSnippet = (src, title) => {
  const origin = (() => { try { return new URL(src).origin; } catch { return ''; } })();
  return `<iframe src="${src}" title="${String(title).replace(/"/g, '&quot;')}" width="100%" height="190" style="border:0;width:100%;max-width:520px" loading="lazy"></iframe>\n<script src="${origin}/embed/resize.js" async></script>`;
};

export default function DonationsPage({ tenantId }) {
  const params = useParams();
  const tenant = tenantId || params.tenant;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tab, setTab] = useState('pages');
  const [settings, setSettings] = useState(null);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [pages, setPages] = useState([]);
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [gifts, setGifts] = useState(null);
  const [filters, setFilters] = useState({ pageId: '', status: '', q: '' });
  const [monthly, setMonthly] = useState(null);
  const [wording, setWording] = useState({ receiptIntro: '', taxStatement: '' });
  const [year, setYear] = useState(() => { const d = new Date(); return d.getMonth() < 6 ? d.getFullYear() - 1 : d.getFullYear(); });
  const [summary, setSummary] = useState(null);
  const [preview, setPreview] = useState(null);

  const loadPages = useCallback(async () => {
    const r = await listDonationPages(tenant);
    setPages(r.pages || []);
  }, [tenant]);

  useEffect(() => {
    let alive = true;
    getDonationSettings(tenant)
      .then((s) => {
        if (!alive) return;
        setSettings(s);
        if (s.settings) setWording(s.settings);
        if (s.ready) loadPages().catch((err) => setError(errorText(err, t('donations.loadError', 'Donations could not be loaded.'))));
      })
      .catch((err) => {
        if (!alive) return;
        if (err?.response?.status === 403) setDenied(true);
        else setError(errorText(err, t('donations.loadError', 'Donations could not be loaded.')));
      });
    getJson(`/api/events/${tenant}/events`).then((d) => { if (alive) setEvents(d.data || d.events || []); }).catch(() => {});
    return () => { alive = false; };
  }, [tenant, loadPages, t]);

  const loadGifts = useCallback(async (before = null) => {
    try {
      const r = await listDonations(tenant, { ...filters, before });
      setGifts((g) => ({ list: before && g ? [...g.list, ...r.donations] : r.donations, next: r.next }));
    } catch (err) { setError(errorText(err, t('donations.loadError', 'Donations could not be loaded.'))); }
  }, [tenant, filters, t]);

  useEffect(() => {
    if (!settings || !settings.ready) return;
    if (tab === 'gifts') loadGifts();
    if (tab === 'monthly') listMonthlyDonors(tenant).then((r) => setMonthly(r.monthly || [])).catch((err) => setError(errorText(err, t('donations.loadError', 'Donations could not be loaded.'))));
    if (tab === 'yearEnd') {
      setSummary(null); setPreview(null);
      listYearSummaries(tenant, year).then(setSummary).catch((err) => setError(errorText(err, t('donations.year.loadError', 'Year-end summaries could not be loaded.'))));
    }
  }, [tab, settings, tenant, year, loadGifts, t]);

  const flash = (msg) => { setNotice(msg); setError(''); };

  const savePage = async (body) => {
    if (editing && editing !== 'new') await updateDonationPage(tenant, editing.id, body);
    else await createDonationPage(tenant, body);
    setEditing(null);
    flash(t('donations.page.saved', 'Donation page saved.'));
    await loadPages();
  };
  const removePage = async (p) => {
    if (!window.confirm(t('donations.page.confirmDelete', 'Delete “{{title}}”? A page that already has gifts is taken offline instead, and its gifts are kept.', { title: p.title }))) return;
    try {
      const r = await deleteDonationPage(tenant, p.id);
      flash(r.archived ? t('donations.page.archived', 'The page has gifts, so it was taken offline instead of deleted.') : t('donations.page.deleted', 'Donation page deleted.'));
      await loadPages();
    } catch (err) { setError(errorText(err, t('donations.page.saveError', 'The donation page could not be saved.'))); }
  };
  const refund = async (g) => {
    const left = (g.totalCents - g.refundedCents) / 100;
    const input = window.prompt(t('donations.gifts.refundPrompt', 'Refund how much? Up to {{amount}}. Leave as is for a full refund.', { amount: formatMoney(g.totalCents - g.refundedCents, g.currency) }), String(left));
    if (input === null) return;
    try {
      const cents = Math.round(Number(input) * 100);
      await refundDonation(tenant, g.id, cents === g.totalCents - g.refundedCents ? null : cents);
      flash(t('donations.gifts.refunded', 'Refund sent.'));
      await loadGifts();
    } catch (err) { setError(errorText(err, t('donations.gifts.refundError', 'The refund didn’t go through.'))); }
  };
  const resend = async (g) => {
    try { await resendReceipt(tenant, g.id); flash(t('donations.gifts.receiptSent', 'Receipt sent to {{email}}.', { email: g.email })); } catch (err) { setError(errorText(err, t('donations.gifts.receiptError', 'The receipt could not be sent.'))); }
  };
  const stopMonthly = async (m) => {
    if (!window.confirm(t('donations.monthly.confirm', 'Stop {{name}}’s monthly gift? No further payments will be taken.', { name: m.name }))) return;
    try {
      await cancelMonthlyGift(tenant, m.id);
      flash(t('donations.monthly.stopped', 'Monthly gift stopped.'));
      setMonthly((l) => l.map((x) => (x.id === m.id ? { ...x, status: 'canceled' } : x)));
    } catch (err) { setError(errorText(err, t('donations.monthly.stopError', 'The monthly gift could not be stopped.'))); }
  };
  const saveWording = async (e) => {
    e.preventDefault();
    try {
      const r = await saveDonationSettings(tenant, wording);
      setWording(r.settings);
      flash(t('donations.receipts.saved', 'Receipt wording saved.'));
    } catch (err) { setError(errorText(err, t('donations.receipts.saveError', 'The receipt wording could not be saved.'))); }
  };
  const showPreview = async (email) => {
    try { setPreview(await previewYearSummary(tenant, year, email)); } catch (err) { setError(errorText(err, t('donations.year.loadError', 'Year-end summaries could not be loaded.'))); }
  };
  const sendSummaries = async (emails) => {
    const count = emails ? emails.length : summary.donors.length;
    if (!window.confirm(t('donations.year.confirm', 'Email {{count}} giving summaries for {{year}}?', { count, year }))) return;
    try {
      const r = await sendYearSummaries(tenant, year, emails);
      flash(t('donations.year.sent', '{{sent}} summaries sent.', { sent: r.sent }) + (r.failed.length ? ` ${t('donations.year.failed', '{{count}} failed.', { count: r.failed.length })}` : ''));
      setSummary(await listYearSummaries(tenant, year));
    } catch (err) { setError(errorText(err, t('donations.year.sendError', 'The summaries could not be sent.'))); }
  };

  const buttons = [
    { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(-1) },
    { icon: <FaCog />, label: t('donations.paymentSettings', 'Payment settings'), onClick: () => navigate(`/${tenant}/settings`) },
  ];
  const statusLabel = {
    completed: t('donations.status.completed', 'Paid'), refunded: t('donations.status.refunded', 'Refunded'),
    pending: t('donations.status.pending', 'Pending'), failed: t('donations.status.failed', 'Failed'),
    active: t('donations.status.active', 'Active'), past_due: t('donations.status.pastDue', 'Payment failed'), canceled: t('donations.status.canceled', 'Stopped'),
  };
  const pay = settings && settings.payments;
  const tabs = [
    ['pages', t('donations.tab.pages', 'Pages')], ['gifts', t('donations.tab.gifts', 'Gifts')], ['monthly', t('donations.tab.monthly', 'Monthly donors')],
    ['receipts', t('donations.tab.receipts', 'Receipts')], ['yearEnd', t('donations.tab.yearEnd', 'Year-end summaries')],
  ];

  return (
    <div className="pb-page">
      <DesktopActionBar buttons={buttons} />
      <section className="surface">
        <h2>{t('donations.title', 'Donations')}</h2>
        <p className="surface-muted">
          {t('donations.intro', 'Take one-time and monthly gifts on your own donation pages. Money goes straight to your organization’s payment account. Each gift adds to the donor’s profile, your audience segments and your reports, and the donor gets a receipt by email.')}
        </p>
      </section>

      {!settings && !error && !denied && <LoadingState />}
      {denied && <div className="note info" role="status">{t('donations.denied', 'Owners, admins and managers manage donations.')}</div>}
      {error && <div className="note error" role="alert">{error}</div>}
      {notice && <div className="note ok" role="status">{notice}</div>}
      {settings && !settings.ready && <div className="note warn" role="status">{t('donations.migration', 'Donations need a database update before they can be used.')}</div>}

      {settings && settings.ready && pay && (
        <section className={`note ${pay.mode === 'none' ? 'warn' : 'ok'}`} data-testid="payment-status">
          {pay.mode === 'connect' && t('donations.pay.connect', 'Gifts are paid out to your Stripe account. One-time and monthly giving are available.')}
          {pay.mode === 'square' && t('donations.pay.square', 'Gifts go to your Square account. Square takes one-time gifts only; monthly giving needs Stripe.')}
          {pay.mode === 'none' && pay.reason === 'stripe_keys_unsupported' && t('donations.pay.keys', 'Donations need Stripe connected through “Set up payouts with Stripe” in Settings → Payments (pasted Stripe keys aren’t supported for donations), or a Square account.')}
          {pay.mode === 'none' && pay.reason !== 'stripe_keys_unsupported' && t('donations.pay.none', 'Connect a payment account in Settings → Payments (Stripe payouts or Square) before you share a donation page. Until then the page says you aren’t taking gifts online yet.')}
        </section>
      )}

      {settings && settings.ready && (
        <>
          <div className="pb-tabs" role="tablist" aria-label={t('donations.title', 'Donations')}>
            {tabs.map(([k, label]) => (
              <button key={k} type="button" role="tab" className="pb-tab" aria-selected={tab === k} onClick={() => { setTab(k); setNotice(''); setError(''); }}>{label}</button>
            ))}
          </div>

          {tab === 'pages' && (
            <section className="surface" data-testid="pages">
              <div className="pb-actions" style={{ justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0 }}>{t('donations.pages.title', 'Donation pages')}</h3>
                {editing === null && <button type="button" className="pb-btn primary" onClick={() => setEditing('new')}><FaPlus aria-hidden="true" /> {t('donations.pages.new', 'New donation page')}</button>}
              </div>
              {editing !== null && <PageForm initial={editing === 'new' ? null : editing} events={events} onSave={savePage} onCancel={() => setEditing(null)} t={t} />}
              {editing === null && pages.length === 0 && <p className="pb-ai-meta">{t('donations.pages.empty', 'No donation pages yet. Make one for each campaign or appeal.')}</p>}
              {editing === null && pages.map((p) => (
                <article key={p.id} className="pb-feed" data-testid="donation-page">
                  <div className="pb-actions" style={{ justifyContent: 'space-between' }}>
                    <p className="pb-feed-title" style={{ margin: 0 }}>{p.title}</p>
                    <span className={`pb-badge ${p.active ? 'ok' : 'muted'}`}>{p.active ? t('donations.pages.live', 'Live') : t('donations.pages.off', 'Offline')}</span>
                  </div>
                  <div className="dn-progress-mini">
                    <GoalProgressBar raisedCents={p.raisedCents} goalCents={p.goalCents} donors={p.donors} currency={p.currency} />
                  </div>
                  {p.url && <CopyField label={t('donations.pages.link', 'Donation page link')} value={p.url} t={t} />}
                  {p.embedUrl && <CopyField label={t('donations.pages.embed', 'Goal bar for your website')} value={embedSnippet(p.embedUrl, p.title)} t={t} />}
                  <div className="pb-actions">
                    <button type="button" className="pb-btn" onClick={() => setEditing(p)}><FaEdit aria-hidden="true" /> {t('donations.edit', 'Edit')}</button>
                    <button type="button" className="pb-btn danger" onClick={() => removePage(p)}><FaTrash aria-hidden="true" /> {t('donations.delete', 'Delete')}</button>
                  </div>
                </article>
              ))}
            </section>
          )}

          {tab === 'gifts' && (
            <section className="surface" data-testid="gifts">
              <form className="pb-form" onSubmit={(e) => { e.preventDefault(); loadGifts(); }}>
                <label className="pb-field" htmlFor="dg-page">
                  {t('donations.gifts.page', 'Page')}
                  <select id="dg-page" value={filters.pageId} onChange={(e) => setFilters((f) => ({ ...f, pageId: e.target.value }))}>
                    <option value="">{t('donations.gifts.allPages', 'All pages')}</option>
                    {pages.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                </label>
                <label className="pb-field" htmlFor="dg-status">
                  {t('donations.gifts.status', 'Status')}
                  <select id="dg-status" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
                    <option value="">{t('donations.gifts.paidOrRefunded', 'Paid and refunded')}</option>
                    <option value="pending">{statusLabel.pending}</option>
                    <option value="failed">{statusLabel.failed}</option>
                  </select>
                </label>
                <label className="pb-field" htmlFor="dg-q">
                  {t('donations.gifts.search', 'Name, email or receipt')}
                  <input id="dg-q" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
                </label>
                <div className="pb-actions pb-wide"><button type="submit" className="pb-btn">{t('donations.gifts.filter', 'Show')}</button></div>
              </form>
              {!gifts && <LoadingState />}
              {gifts && gifts.list.length === 0 && <p className="pb-ai-meta">{t('donations.gifts.empty', 'No gifts yet.')}</p>}
              {gifts && gifts.list.length > 0 && (
                <div className="pb-table-wrap">
                  <table className="pb-table">
                    <thead><tr>
                      <th>{t('donations.gifts.date', 'Date')}</th><th>{t('donations.gifts.donor', 'Donor')}</th><th>{t('donations.gifts.page', 'Page')}</th>
                      <th>{t('donations.gifts.amount', 'Gift')}</th><th>{t('donations.gifts.status', 'Status')}</th><th>{t('donations.gifts.actions', 'Actions')}</th>
                    </tr></thead>
                    <tbody>
                      {gifts.list.map((g) => (
                        <tr key={g.id}>
                          <td>{when(g.completedOn || g.createdOn)}</td>
                          <td>{g.name}<br /><span className="pb-ai-meta">{g.email}</span></td>
                          <td>{g.pageTitle}</td>
                          <td>
                            {formatMoney(g.amountCents, g.currency)}
                            {g.frequency === 'monthly' && <> <span className="pb-badge muted">{t('donations.gifts.monthly', 'monthly')}</span></>}
                            {g.feeCoverCents > 0 && <><br /><span className="pb-ai-meta">{t('donations.gifts.plusFees', '+{{amount}} fees', { amount: formatMoney(g.feeCoverCents, g.currency) })}</span></>}
                            {g.refundedCents > 0 && <><br /><span className="pb-ai-meta">{t('donations.gifts.refundedAmount', '{{amount}} refunded', { amount: formatMoney(g.refundedCents, g.currency) })}</span></>}
                          </td>
                          <td>{statusLabel[g.status] || g.status}{g.receiptNumber && <><br /><span className="pb-ai-meta">{g.receiptNumber}</span></>}</td>
                          <td>
                            <div className="pb-actions">
                              {['completed', 'refunded'].includes(g.status) && <button type="button" className="pb-btn" onClick={() => resend(g)}>{t('donations.gifts.resend', 'Resend receipt')}</button>}
                              {g.status === 'completed' && <button type="button" className="pb-btn danger" onClick={() => refund(g)}>{t('donations.gifts.refund', 'Refund')}</button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {gifts && gifts.next && <button type="button" className="pb-btn" onClick={() => loadGifts(gifts.next)}>{t('donations.gifts.more', 'Show more')}</button>}
            </section>
          )}

          {tab === 'monthly' && (
            <section className="surface" data-testid="monthly">
              <h3>{t('donations.monthly.title', 'Monthly donors')}</h3>
              <p className="pb-ai-meta">{t('donations.monthly.help', 'Each month’s payment shows in Gifts with its own receipt. Donors can stop their gift from the link in any receipt.')}</p>
              {!monthly && <LoadingState />}
              {monthly && monthly.length === 0 && <p className="pb-ai-meta">{t('donations.monthly.empty', 'No monthly donors yet.')}</p>}
              {monthly && monthly.length > 0 && (
                <div className="pb-table-wrap">
                  <table className="pb-table">
                    <thead><tr>
                      <th>{t('donations.gifts.donor', 'Donor')}</th><th>{t('donations.gifts.page', 'Page')}</th><th>{t('donations.monthly.amount', 'Each month')}</th>
                      <th>{t('donations.monthly.paid', 'Months paid')}</th><th>{t('donations.gifts.status', 'Status')}</th><th>{t('donations.gifts.actions', 'Actions')}</th>
                    </tr></thead>
                    <tbody>
                      {monthly.map((m) => (
                        <tr key={m.id}>
                          <td>{m.name}<br /><span className="pb-ai-meta">{m.email}</span></td>
                          <td>{m.pageTitle}</td>
                          <td>{formatMoney(m.amountCents, m.currency)}</td>
                          <td>{m.monthsPaid}</td>
                          <td>{statusLabel[m.status] || m.status}</td>
                          <td>{m.status !== 'canceled' && <button type="button" className="pb-btn danger" onClick={() => stopMonthly(m)}>{t('donations.monthly.stop', 'Stop')}</button>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {tab === 'receipts' && (
            <section className="surface" data-testid="receipts">
              <h3>{t('donations.receipts.title', 'Receipt wording')}</h3>
              <p className="pb-ai-meta">{t('donations.receipts.help', 'Every receipt shows the gift, date, receipt number, any dedication, and the tax statement below. Check the tax statement with your accountant.')}</p>
              <form className="pb-form" onSubmit={saveWording}>
                <label className="pb-field pb-wide" htmlFor="dr-intro">
                  {t('donations.receipts.intro', 'Thank-you message at the top')}
                  <textarea id="dr-intro" rows={3} value={wording.receiptIntro} maxLength={2000}
                    placeholder={t('donations.receipts.introDefault', 'Thank you for your gift.')}
                    onChange={(e) => setWording((w) => ({ ...w, receiptIntro: e.target.value }))} />
                </label>
                <label className="pb-field pb-wide" htmlFor="dr-tax">
                  {t('donations.receipts.tax', 'Tax statement (leave blank for the default below)')}
                  <textarea id="dr-tax" rows={4} value={wording.taxStatement} maxLength={2000}
                    onChange={(e) => setWording((w) => ({ ...w, taxStatement: e.target.value }))} />
                </label>
                <div className="pb-preview pb-wide">
                  <strong>{t('donations.receipts.default', 'Default tax statement')}</strong>
                  <p style={{ margin: '6px 0 0' }}>{settings.defaultTaxStatement}</p>
                </div>
                <div className="pb-actions pb-wide">
                  <button type="submit" className="pb-btn primary">{t('donations.receipts.save', 'Save wording')}</button>
                  {wording.taxStatement && <button type="button" className="pb-btn" onClick={() => setWording((w) => ({ ...w, taxStatement: '' }))}>{t('donations.receipts.useDefault', 'Use the default statement')}</button>}
                </div>
              </form>
            </section>
          )}

          {tab === 'yearEnd' && (
            <section className="surface" data-testid="year-end">
              <h3>{t('donations.year.title', 'Year-end giving summaries')}</h3>
              <p className="pb-ai-meta">{t('donations.year.help', 'One email per donor listing every gift in the year (after refunds), with your tax statement. Years follow your organization’s time zone.')}</p>
              <div className="pb-actions">
                <label className="pb-field" htmlFor="dy-year">
                  {t('donations.year.year', 'Year')}
                  <select id="dy-year" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                    {[0, 1, 2, 3].map((n) => { const y = new Date().getFullYear() - n; return <option key={y} value={y}>{y}</option>; })}
                  </select>
                </label>
                {summary && summary.donors.length > 0 && (
                  <button type="button" className="pb-btn primary" onClick={() => sendSummaries(null)}>{t('donations.year.sendAll', 'Email all donors')}</button>
                )}
              </div>
              {!summary && <LoadingState />}
              {summary && summary.donors.length === 0 && <p className="pb-ai-meta">{t('donations.year.empty', 'No gifts in {{year}}.', { year })}</p>}
              {summary && summary.donors.length > 0 && (
                <div className="pb-table-wrap">
                  <table className="pb-table">
                    <thead><tr>
                      <th>{t('donations.gifts.donor', 'Donor')}</th><th>{t('donations.year.gifts', 'Gifts')}</th><th>{t('donations.year.total', 'Total')}</th>
                      <th>{t('donations.year.sentOn', 'Sent')}</th><th>{t('donations.gifts.actions', 'Actions')}</th>
                    </tr></thead>
                    <tbody>
                      {summary.donors.map((d) => (
                        <tr key={d.email}>
                          <td>{d.name}<br /><span className="pb-ai-meta">{d.email}</span></td>
                          <td>{d.gifts}</td>
                          <td>{formatMoney(d.totalCents)}</td>
                          <td>{d.sentOn ? when(d.sentOn) : '—'}</td>
                          <td>
                            <div className="pb-actions">
                              <button type="button" className="pb-btn" onClick={() => showPreview(d.email)}>{t('donations.year.preview', 'Preview')}</button>
                              <button type="button" className="pb-btn" onClick={() => sendSummaries([d.email])}>{t('donations.year.send', 'Email')}</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {preview && (
                <div className="pb-preview" data-testid="summary-preview">
                  <div className="pb-actions" style={{ justifyContent: 'space-between' }}>
                    <strong>{preview.subject}</strong>
                    <button type="button" className="pb-btn" onClick={() => setPreview(null)}>{t('common.close', 'Close')}</button>
                  </div>
                  <iframe title={preview.subject} sandbox="" srcDoc={preview.html} style={{ width: '100%', minHeight: 360, border: '1px solid #d8dee9', borderRadius: 8, background: '#ffffff' }} />
                </div>
              )}
            </section>
          )}
        </>
      )}
      <AppFooter buttons={buttons} />
    </div>
  );
}
